/**
 * デフォルトコスト上限設定カード
 *
 * ダッシュボードに表示し、デフォルトの月額コスト上限をインライン編集できる。
 */
import { DollarSign, Loader2, Pencil, Trash2, X } from 'lucide-react';
import { useCallback, useState } from 'react';
import {
  useCostLimits,
  useDeleteDefaultCostLimit,
  useSetDefaultCostLimit,
} from '../../hooks/use-admin-api';
import { formatCurrency } from './chart-utils';

export function CostLimitCard() {
  const { data: costLimits, isLoading } = useCostLimits();
  const setDefaultLimit = useSetDefaultCostLimit();
  const deleteDefaultLimit = useDeleteDefaultCostLimit();

  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');

  const defaultLimit = costLimits?.defaultLimit;
  const userLimitsCount = costLimits?.userLimits?.length ?? 0;

  const handleEdit = useCallback(() => {
    setEditValue(defaultLimit ? defaultLimit.limitUSD.toString() : '');
    setIsEditing(true);
  }, [defaultLimit]);

  const handleCancel = useCallback(() => {
    setIsEditing(false);
    setEditValue('');
  }, []);

  const handleSave = useCallback(async () => {
    const value = Number.parseFloat(editValue);
    if (Number.isNaN(value) || value < 0) return;
    await setDefaultLimit.mutateAsync(value);
    setIsEditing(false);
  }, [editValue, setDefaultLimit]);

  const handleDelete = useCallback(async () => {
    await deleteDefaultLimit.mutateAsync();
  }, [deleteDefaultLimit]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') handleSave();
      if (e.key === 'Escape') handleCancel();
    },
    [handleSave, handleCancel],
  );

  const isMutating = setDefaultLimit.isPending || deleteDefaultLimit.isPending;

  return (
    <div className="bg-card rounded-xl p-6 border border-border">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <DollarSign className="w-5 h-5" />
          月額コスト上限
        </h2>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="space-y-4">
          {/* デフォルト上限 */}
          <div>
            <div className="text-sm text-muted-foreground mb-1">デフォルト上限</div>
            {isEditing ? (
              <div className="flex items-center gap-2">
                <span className="text-foreground">$</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className="w-32 px-2 py-1 bg-muted border border-border rounded text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                  // biome-ignore lint/a11y/noAutofocus: inline edit needs auto-focus for UX
                  autoFocus
                  disabled={isMutating}
                />
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={isMutating}
                  className="px-3 py-1 text-xs bg-primary-500 text-white rounded hover:bg-primary-600 transition-colors disabled:opacity-50"
                >
                  {isMutating ? <Loader2 className="w-3 h-3 animate-spin" /> : '保存'}
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  disabled={isMutating}
                  className="p-1 text-muted-foreground hover:text-foreground transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <span className="text-xl font-bold text-foreground">
                  {defaultLimit ? formatCurrency(defaultLimit.limitUSD) : '未設定'}
                </span>
                <button
                  type="button"
                  onClick={handleEdit}
                  className="p-1 text-muted-foreground hover:text-foreground transition-colors"
                  title="編集"
                >
                  <Pencil className="w-4 h-4" />
                </button>
                {defaultLimit && (
                  <button
                    type="button"
                    onClick={handleDelete}
                    disabled={isMutating}
                    className="p-1 text-muted-foreground hover:text-red-400 transition-colors disabled:opacity-50"
                    title="削除"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            )}
          </div>

          {/* 説明 */}
          <p className="text-xs text-muted-foreground">
            個別上限が未設定のすべてのユーザーにこのデフォルト上限が適用されます。
          </p>

          {/* 個別上限設定済みユーザー数 */}
          <div className="pt-2 border-t border-border">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">個別上限設定済み</span>
              <span className="text-foreground">{userLimitsCount} ユーザー</span>
            </div>
          </div>

          {/* 更新情報 */}
          {defaultLimit && (
            <div className="text-xs text-muted-foreground">
              最終更新: {new Date(defaultLimit.updatedAt).toLocaleString('ja-JP')} (
              {defaultLimit.updatedBy})
            </div>
          )}
        </div>
      )}
    </div>
  );
}
